// Data adapter that integrates Klassen Buch mock data with the web-app system
// This provides the same API as the original mockData but can be swapped for real data later

// Re-export the types and data from the local mockData copy
export type {
  Student,
  Lesson,
  Course,
  CourseStudent,
  AttendanceRecord,
  ExcuseEditHistory,
  ExcuseInfo,
  CourseAttendanceEntry,
  AbsenceDetail,
  LatenessDetail,
  StudentStatistics,
} from "../../data/mockData";

// New types for database-driven data
export interface SchedulePeriod {
  id: string;
  school_id: string;
  block_number: number;
  start_time: string; // time format: "08:00:00"
  end_time: string; // time format: "08:45:00"
  label: string;
  group_label?: string;
  attendance_requirement: "required" | "flexible" | "contracted";
  block_type:
    | "instructional"
    | "break"
    | "flex"
    | "before_school"
    | "after_school"
    | "admin"
    | "custom";
  created_by?: string;
}

export interface StructureDay {
  id: number;
  name_en: string;
  name_de?: string;
  day_number: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  order_index: number;
}

export interface SchoolDay {
  school_id: string;
  day_id: number;
  day: StructureDay;
}

export interface CalendarException {
  id: string;
  startDate: string;
  endDate: string;
  type: string;
  allowCheckins: boolean;
  name: string;
  source: string | null;
  isGlobal: boolean;
}

// Import and re-export all the data and helper functions
export {
  CURRENT_TEACHER,
  SEMESTER_START_DATE,
  getCurrentDate,
  mockStudents,
  mockCourses,
  mockCourseStudents,
  mockTimetables,
  teacherSchedule,
  mockStudentStatistics,
  mockCourseData,
  getStudentsForClass,
  getTeacherLessons,
  getStudentStatisticsForClass,
  getAllStudentStatistics,
  getCourseDataForClass,
  getCourseStudentsForCourse,
  getAllCoursesAndClasses,
  getClassesForStatistics,
  getCoursesForStatistics,
  getAvailableClasses,
  getStudentById,
  updateAbsenceDetails,
  updateLatenessDetails,
  convertToExcused,
  getStudentIdFromCourseName,
  getClassNameById,
} from "../../data/mockData";

// Example: adapter function to format data for the main dashboard
export function getKlassenbuchSummary() {
  // This could provide a summary of Klassenbuch data for dashboard widgets
  return {
    totalClasses: 3,
    pendingAttendance: 5,
    recentChanges: 2,
  };
}

// Import Supabase client
import { supabase, getCurrentUserProfile } from "../../../../lib/supabase.js";

// Cache for user profile to avoid repeated fetches
let cachedUserProfile: any = null;
let profileCacheTime: number = 0;

/**
 * Efficiently check attendance access permissions for multiple lessons in batch
 * @param lessons - Array of lessons to check access for
 * @returns Promise<Record<string, boolean>> - Map of lesson IDs to access permissions
 */
export async function batchCheckAttendanceAccess(
  lessons: Lesson[],
): Promise<Record<string, boolean>> {
  try {
    if (lessons.length === 0) {
      return {};
    }

    console.log(
      "🔒 Batch checking attendance access for",
      lessons.length,
      "lessons",
    );

    // Get current user profile once
    const userProfile = await getCurrentUserProfile();
    if (!userProfile) {
      console.log("❌ No user profile found for batch attendance access check");
      return Object.fromEntries(lessons.map((l) => [l.id, false]));
    }

    // Check if user has admin role (full access)
    const adminRoles = ["Admin", "Super Admin"];

    // Check direct role
    let hasAdminRole =
      userProfile.roles?.name && adminRoles.includes(userProfile.roles.name);

    // Check multiple roles if not admin yet
    if (!hasAdminRole) {
      const { data: userRoles, error } = await supabase
        .from("user_roles")
        .select("*, roles(name)")
        .eq("user_profile_id", userProfile.id);

      if (!error && userRoles) {
        hasAdminRole = userRoles.some(
          (ur) => ur.roles?.name && adminRoles.includes(ur.roles.name),
        );
      }
    }

    if (hasAdminRole) {
      console.log("✅ Admin user has full attendance access to all lessons");
      return Object.fromEntries(lessons.map((l) => [l.id, true]));
    }

    // For non-admin users, also get classes this staff is linked to
    const { data: linkedRows } = await supabase
      .from("staff_class_links")
      .select("class_id")
      .eq("staff_id", userProfile.id)
      .eq("school_id", userProfile.school_id);
    const linkedClassIds = new Set<string>(
      (linkedRows || []).map((r: any) => r.class_id).filter(Boolean),
    );

    // Get all lesson assignments (and class_id) in one query
    const lessonIds = lessons.map((l) => l.id);
    // Use vw_react_lesson_details_all to avoid RLS blocking lessons we need to check access for
    const { data: lessonData, error: lessonError } = await supabase
      .from("vw_react_lesson_details_all")
      .select(
        "lesson_id, class_id, teacher_ids, primary_teacher_id, assigned_teacher_id, substitute_staff_id, substitute_begleitung_teacher_ids",
      )
      .in("lesson_id", lessonIds);

    if (lessonError || !lessonData) {
      console.error(
        "❌ Error fetching lesson data for batch attendance access check:",
        lessonError,
      );
      return Object.fromEntries(lessons.map((l) => [l.id, false]));
    }

    console.log("🔍 DEBUG batchCheckAttendanceAccess:", {
      requestedLessonIds: lessonIds,
      returnedLessonCount: lessonData.length,
      returnedLessonIds: lessonData.map((ld) => ld.lesson_id),
    });

    // Process permissions in memory
    const permissions: Record<string, boolean> = {};
    const userProfileId = userProfile.id;

    for (const lesson of lessons) {
      const lessonDetails = lessonData.find((ld) => ld.lesson_id === lesson.id);

      if (!lessonDetails) {
        console.log(
          `⚠️ No lesson details found for ${lesson.id} (${lesson.subject})`,
        );
        permissions[lesson.id] = false;
        continue;
      }

      // Check if user is teaching this lesson or is a substitute
      const teacherIds = lessonDetails.teacher_ids || [];
      const userIsTeaching = teacherIds.includes(userProfileId);
      const userIsPrimaryTeacher =
        lessonDetails.primary_teacher_id === userProfileId;
      const userIsAssignedTeacher =
        lessonDetails.assigned_teacher_id === userProfileId;
      const userIsSubstituteTeacher =
        lessonDetails.substitute_staff_id === userProfileId;
      // Also check if user is a substitute begleitung teacher (for zusätzlicher Einsatz)
      const substituteBegleitungIds =
        (lessonDetails as any).substitute_begleitung_teacher_ids || [];
      const userIsSubstituteBegleitung =
        Array.isArray(substituteBegleitungIds) &&
        substituteBegleitungIds.includes(userProfileId);

      const userIsLinkedToClass = lessonDetails?.class_id
        ? linkedClassIds.has(lessonDetails.class_id)
        : false;
      const hasAccess =
        userIsTeaching ||
        userIsPrimaryTeacher ||
        userIsAssignedTeacher ||
        userIsSubstituteTeacher ||
        userIsSubstituteBegleitung ||
        userIsLinkedToClass;
      permissions[lesson.id] = hasAccess;

      console.log(
        `🔍 Lesson ${lesson.id} (${lesson.subject}): access = ${hasAccess}`,
        {
          userIsTeaching,
          userIsPrimaryTeacher,
          userIsAssignedTeacher,
          userIsSubstituteTeacher,
          userIsSubstituteBegleitung,
          userIsLinkedToClass,
        },
      );
    }

    console.log(
      "✅ Batch attendance access permissions computed:",
      permissions,
    );
    return permissions;
  } catch (error) {
    console.error("💥 Error in batch attendance access check:", error);
    return Object.fromEntries(lessons.map((l) => [l.id, false]));
  }
}
const PROFILE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get the current user's school ID with caching
 * @returns Promise<string | null> - The school ID or null if not found
 */
export async function getCurrentSchoolId(): Promise<string | null> {
  try {
    const userProfile = await getCachedUserProfile();
    return userProfile?.school_id || null;
  } catch (error) {
    console.error("💥 Error getting current school ID:", error);
    return null;
  }
}

/**
 * Get user profile with caching to avoid repeated database calls
 */
async function getCachedUserProfile() {
  const now = Date.now();

  // Return cached profile if it's still valid (within 5 minutes)
  if (cachedUserProfile && now - profileCacheTime < PROFILE_CACHE_DURATION) {
    return cachedUserProfile;
  }

  // Fetch fresh profile and cache it
  cachedUserProfile = await getCurrentUserProfile();
  profileCacheTime = now;

  return cachedUserProfile;
}

/**
 * Get schedule periods for a school filtered by block_type
 * @param schoolId - The school ID
 * @param blockTypes - Array of block types to filter by (default: ['instructional', 'flex'])
 * @returns Promise<Array of schedule periods>
 */
export async function getSchedulePeriods(
  schoolId: string,
  blockTypes: string[] = ["instructional", "flex"],
): Promise<SchedulePeriod[]> {
  try {
    const { data: periods, error } = await supabase
      .from("schedule_periods")
      .select("*")
      .in("block_type", blockTypes)
      .order("block_number", { ascending: true });

    if (error) {
      try {
        console.error(
          "❌ Error fetching schedule periods:",
          JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
        );
      } catch (e) {
        console.error("❌ Error fetching schedule periods:", error);
      }
      throw error;
    }

    return periods || [];
  } catch (error) {
    try {
      console.error(
        "💥 Error in getSchedulePeriods:",
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      );
    } catch (e) {
      console.error("💥 Error in getSchedulePeriods:", error);
    }
    // Return fallback periods to prevent UI from breaking
    return [
      {
        id: "1",
        school_id: schoolId,
        block_number: 1,
        start_time: "08:00:00",
        end_time: "08:45:00",
        label: "1. Stunde",
        group_label: "Vormittag",
        attendance_requirement: "required",
        block_type: "instructional",
      },
      {
        id: "2",
        school_id: schoolId,
        block_number: 2,
        start_time: "08:50:00",
        end_time: "09:35:00",
        label: "2. Stunde",
        group_label: "Vormittag",
        attendance_requirement: "required",
        block_type: "instructional",
      },
      {
        id: "3",
        school_id: schoolId,
        block_number: 3,
        start_time: "09:55:00",
        end_time: "10:40:00",
        label: "3. Stunde",
        group_label: "Vormittag",
        attendance_requirement: "required",
        block_type: "instructional",
      },
      {
        id: "4",
        school_id: schoolId,
        block_number: 4,
        start_time: "10:45:00",
        end_time: "11:30:00",
        label: "4. Stunde",
        group_label: "Vormittag",
        attendance_requirement: "required",
        block_type: "instructional",
      },
      {
        id: "5",
        school_id: schoolId,
        block_number: 5,
        start_time: "11:50:00",
        end_time: "12:35:00",
        label: "5. Stunde",
        group_label: "Nachmittag",
        attendance_requirement: "required",
        block_type: "instructional",
      },
      {
        id: "6",
        school_id: schoolId,
        block_number: 6,
        start_time: "12:40:00",
        end_time: "13:25:00",
        label: "6. Stunde",
        group_label: "Nachmittag",
        attendance_requirement: "required",
        block_type: "instructional",
      },
    ];
  }
}

/**
 * Get operating days for a school
 * @param schoolId - The school ID
 * @returns Promise<Array of school days with day information>
 */
export async function getSchoolDays(schoolId: string): Promise<SchoolDay[]> {
  try {
    // Since there's no foreign key constraint, we need to do separate queries

    // First, get the school days
    const { data: schoolDays, error: schoolDaysError } = await supabase
      .from("structure_school_days")
      .select("school_id, day_id")
      .eq("school_id", schoolId);

    if (schoolDaysError) {
      console.error("❌ Error fetching school days:", schoolDaysError);
      throw schoolDaysError;
    }

    if (!schoolDays || schoolDays.length === 0) {
      return [];
    }

    // Get unique day IDs
    const dayIds = [...new Set(schoolDays.map((sd) => sd.day_id))];

    // Second, get the structure days
    const { data: structureDays, error: structureDaysError } = await supabase
      .from("structure_days")
      .select("id, name_en, name_de, day_number, order_index")
      .in("id", dayIds)
      .order("order_index", { ascending: true });

    if (structureDaysError) {
      console.error("❌ Error fetching structure days:", structureDaysError);
      throw structureDaysError;
    }

    // Manual join: combine the data
    const transformedDays: SchoolDay[] = schoolDays
      .map((schoolDay) => {
        const structureDay = structureDays?.find(
          (sd) => sd.id === schoolDay.day_id,
        );
        if (!structureDay) return null;

        return {
          school_id: schoolDay.school_id,
          day_id: schoolDay.day_id,
          day: {
            id: structureDay.id,
            name_en: structureDay.name_en,
            name_de: structureDay.name_de,
            day_number: structureDay.day_number,
            order_index: structureDay.order_index,
          },
        };
      })
      .filter((day): day is SchoolDay => day !== null)
      .sort((a, b) => a.day.order_index - b.day.order_index);

    return transformedDays;
  } catch (error) {
    try {
      console.error(
        "💥 Error in getSchoolDays:",
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      );
    } catch (e) {
      console.error("💥 Error in getSchoolDays:", error);
    }
    // Return fallback school days (Monday-Friday) to prevent UI from breaking
    return [
      {
        school_id: schoolId,
        day_id: 1,
        day: {
          id: 1,
          name_en: "Monday",
          name_de: "Montag",
          day_number: 1,
          order_index: 1,
        },
      },
      {
        school_id: schoolId,
        day_id: 2,
        day: {
          id: 2,
          name_en: "Tuesday",
          name_de: "Dienstag",
          day_number: 2,
          order_index: 2,
        },
      },
      {
        school_id: schoolId,
        day_id: 3,
        day: {
          id: 3,
          name_en: "Wednesday",
          name_de: "Mittwoch",
          day_number: 3,
          order_index: 3,
        },
      },
      {
        school_id: schoolId,
        day_id: 4,
        day: {
          id: 4,
          name_en: "Thursday",
          name_de: "Donnerstag",
          day_number: 4,
          order_index: 4,
        },
      },
      {
        school_id: schoolId,
        day_id: 5,
        day: {
          id: 5,
          name_en: "Friday",
          name_de: "Freitag",
          day_number: 5,
          order_index: 5,
        },
      },
    ];
  }
}

/**
 * Format time from database format (HH:MM:SS) to display format (HH:MM)
 * @param timeString - Time in format "08:00:00"
 * @returns Time in format "08:00"
 */
export function formatTime(timeString: string): string {
  return timeString.substring(0, 5); // Remove seconds
}

/**
 * Format time slot for display
 * @param period - Schedule period
 * @returns Formatted time slot like "08:00-08:45"
 */
export function formatTimeSlot(period: SchedulePeriod): string {
  return `${formatTime(period.start_time)}-${formatTime(period.end_time)}`;
}

// Interface for database lesson data
interface DatabaseLesson {
  lesson_id: string;
  subject_name: string;
  class_name: string;
  room_name: string;
  room_number: string;
  start_datetime: string;
  end_datetime: string;
  lesson_type: "regular" | "cancelled" | "substitute";
  is_cancelled: boolean;
  teacher_names: string[];
  teacher_kurzels: string[];
  period_number: number;
  period_label: string;
  attendance_taken: boolean;
  student_count: number; // Total students in the lesson
  school_id: string;
  lesson_date: string;
  subject_abbreviation?: string;
  subject_color?: string;
  class_id?: string;
  course_id?: string;
  course_name?: string;
  course_code?: string;
  // Substitute lesson fields
  is_substitute?: boolean;
  substitute_is_cancelled?: boolean;
  substitute_status?: string;
  substitute_comment?: string;
  original_teacher_kurzel?: string;
}

/**
 * Check attendance completion for a lesson
 * @param lessonId - The lesson ID
 * @returns Promise<AttendanceCompletion>
 */
interface AttendanceCompletion {
  status: "complete" | "incomplete" | "missing";
  studentsWithAttendance: number;
  totalStudents: number;
  completionPercentage: number;
}

// Optimized: Batch attendance checking for all lessons at once
async function batchCheckAttendanceCompletion(
  lessonIds: string[],
): Promise<Map<string, number>> {
  try {
    if (lessonIds.length === 0) {
      return new Map();
    }

    // Single query to get attendance counts for all lessons
    const { data: attendanceCounts, error: attendanceError } = await supabase
      .from("student_attendance_logs")
      .select("lesson_id")
      .in("lesson_id", lessonIds);

    if (attendanceError) {
      console.error(
        "❌ Error fetching batch attendance records:",
        attendanceError,
      );
      return new Map();
    }

    // Count attendance records per lesson
    const attendanceMap = new Map<string, number>();

    // Initialize all lessons with 0
    lessonIds.forEach((id) => attendanceMap.set(id, 0));

    // Count actual attendance records
    (attendanceCounts || []).forEach((record) => {
      const current = attendanceMap.get(record.lesson_id) || 0;
      attendanceMap.set(record.lesson_id, current + 1);
    });

    return attendanceMap;
  } catch (error) {
    console.error("���� Error in batch attendance check:", error);
    return new Map();
  }
}

// Optimized lesson transformation with batched attendance checking
async function transformDatabaseLessonsOptimized(
  dbLessons: DatabaseLesson[],
  schoolDays: SchoolDay[],
): Promise<Lesson[]> {
  if (dbLessons.length === 0) {
    return [];
  }

  // Only batch attendance checking if we have lessons that need it
  const lessonsWithAttendance = dbLessons.filter(
    (lesson) => lesson.attendance_taken,
  );
  const attendanceCounts =
    lessonsWithAttendance.length > 0
      ? await batchCheckAttendanceCompletion(
          lessonsWithAttendance.map((lesson) => lesson.lesson_id),
        )
      : new Map();

  // Helper for generating short abbreviations
  const computeInitials = (value: string | null | undefined): string => {
    if (!value) return "";
    const normalized = value.trim();
    if (!normalized) return "";
    const tokens = normalized.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return "";
    if (tokens.length === 1) {
      return tokens[0].slice(0, 3).toUpperCase();
    }
    return tokens
      .slice(0, 3)
      .map((token) => token[0] ?? "")
      .join("")
      .toUpperCase();
  };

  // Transform all lessons using the batched attendance data
  return dbLessons.map((dbLesson) => {
    const now = new Date();
    const lessonStart = new Date(dbLesson.start_datetime);
    const lessonEnd = new Date(dbLesson.end_datetime);

    // Determine if lesson is past, ongoing, or future
    // Allow attendance 10 minutes before the lesson starts
    const TEN_MINUTES_MS = 10 * 60 * 1000;
    const attendanceWindowStart = new Date(
      lessonStart.getTime() - TEN_MINUTES_MS,
    );
    const isPast = lessonEnd < now;
    const isOngoing = attendanceWindowStart <= now && lessonEnd >= now;

    // Get day name using the school's day mapping
    const lessonDayNumber = lessonStart.getDay();
    const schoolDay = schoolDays.find(
      (sd) => sd.day.day_number === lessonDayNumber,
    );
    const dayName =
      schoolDay?.day.name_de || schoolDay?.day.name_en || "Unbekannt";

    // Format time
    const timeString = `${lessonStart
      .toTimeString()
      .substring(0, 5)}-${lessonEnd.toTimeString().substring(0, 5)}`;

    // Determine attendance status with optimized checking
    let attendanceStatus: "complete" | "missing" | "incomplete" | "future" =
      "future";

    if (isPast || isOngoing) {
      if (dbLesson.attendance_taken) {
        const studentsWithAttendance =
          attendanceCounts.get(dbLesson.lesson_id) || 0;
        const totalStudents = dbLesson.student_count || 1;

        if (studentsWithAttendance === 0) {
          attendanceStatus = "missing";
        } else if (studentsWithAttendance >= totalStudents) {
          attendanceStatus = "complete";
        } else {
          attendanceStatus = "incomplete";
        }
      } else {
        attendanceStatus = "missing";
      }
    }

    // Get subject color
    const subjectColors: Record<string, string> = {
      Deutsch: "bg-emerald-100 text-emerald-800",
      Mathematik: "bg-green-100 text-green-800",
      Englisch: "bg-blue-100 text-blue-800",
      DAZ: "bg-red-100 text-red-800",
      Sport: "bg-yellow-100 text-yellow-800",
      Physik: "bg-cyan-100 text-cyan-800",
      Chemie: "bg-violet-100 text-violet-800",
      Biologie: "bg-lime-100 text-lime-800",
      Geschichte: "bg-amber-100 text-amber-800",
      Musik: "bg-pink-100 text-pink-800",
      Kunst: "bg-orange-100 text-orange-800",
      Ethik: "bg-teal-100 text-teal-800",
      Französisch: "bg-rose-100 text-rose-800",
      Politik: "bg-red-100 text-red-800",
    };

    // Differentiate substitution types
    const isProperSub =
      dbLesson.substitute_type === "vertretung" ||
      dbLesson.substitute_type === "begleitung";
    const isAdditionalAssignment =
      dbLesson.substitute_type === "zusaetzlicher_einsatz";
    const incomingIsSub = (dbLesson as any).isSubstituteTeacher === true;
    const isSubstituteLesson = incomingIsSub; // only mark purple if viewer is the UB

    // Prefer DB subject color (hex) from system subjects when available
    const dbHex = (dbLesson as any).subject_color as string | undefined;

    // Base subject color fallback (tailwind classes)
    const fallbackSubjectColor =
      subjectColors[dbLesson.subject_name || ""] || "bg-gray-100 text-gray-800";

    // Do not set background class when we have dbHex; we'll apply inline style in the component
    // For substitute lessons, only use text color (not bg color) to avoid conflicts with purple bg
    const subjectColor = isSubstituteLesson
      ? `${fallbackSubjectColor.replace(
          /bg-\S+/g,
          "",
        )} border-l-4 border-purple-500`
      : dbHex
      ? ""
      : fallbackSubjectColor;

    // Determine teacher display
    let teacherDisplay =
      Array.isArray(dbLesson.teacher_names) && dbLesson.teacher_names.length > 0
        ? dbLesson.teacher_names[0]
        : "N/A";

    const teacherKurzelsRaw: string[] = Array.isArray(dbLesson.teacher_kurzels)
      ? dbLesson.teacher_kurzels
      : [];
    let teacherKurzelsDisplay: string[] = [...teacherKurzelsRaw];
    const teacherIds: string[] = Array.isArray((dbLesson as any).teacher_ids)
      ? (dbLesson as any).teacher_ids
      : [];
    const idToKurzel = new Map<string, string>();
    teacherIds.forEach((id, idx) => {
      const k = teacherKurzelsRaw[idx];
      if (id && k) idToKurzel.set(id, k);
    });
    // Prefer backend absent_teacher_kurzels and fall back to id-based mapping
    const absentIdsArr: string[] = Array.isArray(
      (dbLesson as any).substitute_absent_teacher_ids,
    )
      ? (dbLesson as any).substitute_absent_teacher_ids
      : Array.isArray((dbLesson as any).absent_teacher_ids)
      ? (dbLesson as any).absent_teacher_ids
      : [];
    const primaryId: string | null =
      (dbLesson as any).primary_teacher_id ||
      (dbLesson as any).assigned_teacher_id ||
      null;
    const originalKurzel: string | undefined = (dbLesson as any)
      .original_teacher_kurzel;
    // Check for substitute_absent_teacher_kurzels (from view) first, then fall back to absent_teacher_kurzels
    const backendAbsentKurzels: string[] = Array.isArray(
      (dbLesson as any).substitute_absent_teacher_kurzels,
    )
      ? (dbLesson as any).substitute_absent_teacher_kurzels.filter(Boolean)
      : Array.isArray((dbLesson as any).absent_teacher_kurzels)
      ? (dbLesson as any).absent_teacher_kurzels.filter(Boolean)
      : [];
    const absentTeacherKurzelsSet = new Set<string>(backendAbsentKurzels);

    // Always ensure the original teacher kürzel appears first in the display list when available
    if (originalKurzel && !teacherKurzelsDisplay.includes(originalKurzel)) {
      teacherKurzelsDisplay = [originalKurzel, ...teacherKurzelsDisplay];
    }

    if (absentTeacherKurzelsSet.size === 0 && absentIdsArr.length > 0) {
      absentIdsArr.forEach((id) => {
        const k = idToKurzel.get(id);
        if (k) absentTeacherKurzelsSet.add(k);
      });
    }

    // Final fallback: if primary teacher id is in absent ids and we still have no kürzels, mark the original/primary kurzel
    if (
      absentTeacherKurzelsSet.size === 0 &&
      primaryId &&
      absentIdsArr.includes(primaryId)
    ) {
      if (originalKurzel) {
        absentTeacherKurzelsSet.add(originalKurzel);
      } else if (teacherKurzelsDisplay.length > 0) {
        absentTeacherKurzelsSet.add(teacherKurzelsDisplay[0]);
      }
    }

    // Ensure all absent kürzels are present in the display list
    if (absentTeacherKurzelsSet.size > 0) {
      absentTeacherKurzelsSet.forEach((k) => {
        if (k && !teacherKurzelsDisplay.includes(k)) {
          teacherKurzelsDisplay.push(k);
        }
      });
    }

    // Never mark the currently logged-in substitute as absent in their own timetable
    const viewerTeacherId: string | null =
      (dbLesson as any).viewerTeacherId || null;
    if (viewerTeacherId && absentTeacherKurzelsSet.size > 0) {
      const isAdditional = isAdditionalAssignment;
      const isSubstitutePrimary =
        !isAdditional &&
        (dbLesson as any).substitute_primary_teacher_id === viewerTeacherId;
      const isSubstituteBegleitung =
        !isAdditional &&
        Array.isArray((dbLesson as any).substitute_begleitung_teacher_ids) &&
        (dbLesson as any).substitute_begleitung_teacher_ids.includes(
          viewerTeacherId,
        );
      if (isSubstitutePrimary || isSubstituteBegleitung) {
        const myKurzels = teacherIds.reduce<string[]>((acc, tid, idx) => {
          if (tid === viewerTeacherId && teacherKurzelsRaw[idx]) {
            acc.push(teacherKurzelsRaw[idx]);
          }
          return acc;
        }, []);
        if (myKurzels.length > 0) {
          myKurzels.forEach((k) => absentTeacherKurzelsSet.delete(k));
        }
      }
    }

    // Show Vertretung label only when viewer is UB
    if (isSubstituteLesson) {
      const currentTeacherKurzel = teacherKurzelsDisplay[0] || "N/A";
      const originalTeacherKurzel = dbLesson.original_teacher_kurzel;
      teacherDisplay = originalTeacherKurzel
        ? `${currentTeacherKurzel} (Vertretung für ${originalTeacherKurzel})`
        : `${currentTeacherKurzel} (Vertretung)`;
    }

    // Include Begleitung kurzels in display list if present (for both substitutions and additional assignments)
    const begleitungKurzels = (dbLesson as any)
      .substitute_begleitung_teacher_kurzels as string[] | undefined;
    if (Array.isArray(begleitungKurzels) && begleitungKurzels.length) {
      const set = new Set([
        ...(teacherKurzelsDisplay || []),
        ...begleitungKurzels,
      ]);
      teacherKurzelsDisplay = Array.from(set);
    }

    // Handle room changes for both proper subs and additional assignments
    // Only show "(geändert)" if the substitute room is actually different from the original room
    let roomDisplay = dbLesson.room_number
      ? `Raum: ${dbLesson.room_number}`
      : dbLesson.room_name || "";
    const originalRoom = dbLesson.room_number || dbLesson.room_name || "";
    const substituteRoom =
      (dbLesson as any).substitute_room_number ||
      (dbLesson as any).substitute_room_name ||
      "";
    const roomActuallyChanged =
      substituteRoom && substituteRoom !== originalRoom;

    if (
      (isProperSub || isAdditionalAssignment) &&
      (dbLesson as any).substitute_room_number
    ) {
      roomDisplay = roomActuallyChanged
        ? `Raum: ${(dbLesson as any).substitute_room_number} (geändert)`
        : `Raum: ${(dbLesson as any).substitute_room_number}`;
    } else if (
      (isProperSub || isAdditionalAssignment) &&
      (dbLesson as any).substitute_room_name
    ) {
      roomDisplay = roomActuallyChanged
        ? `${(dbLesson as any).substitute_room_name} (geändert)`
        : `${(dbLesson as any).substitute_room_name}`;
    }

    // Handle comments: prefer substitute_comment if present; otherwise fall back to notes
    let adminComment =
      dbLesson.substitute_comment || dbLesson.notes || undefined;

    const rawCourseName = (dbLesson.course_name ?? "").trim();
    const courseName = rawCourseName.length ? rawCourseName : null;
    const courseCode = (dbLesson.course_code ?? "").trim() || null;
    const subjectName = (dbLesson.subject_name ?? "").trim();
    const displaySubject =
      courseName || subjectName || (dbLesson.subject_abbreviation ?? "") || "";
    const isCourseLesson = Boolean(courseName);

    let subjectAbbreviation = (dbLesson.subject_abbreviation ?? "").trim();
    if (!subjectAbbreviation && courseName) {
      subjectAbbreviation = computeInitials(courseName);
    }
    if (!subjectAbbreviation && subjectName) {
      subjectAbbreviation = computeInitials(subjectName);
    }
    if (!subjectAbbreviation && courseCode) {
      subjectAbbreviation = courseCode.slice(0, 6).toUpperCase();
    }
    if (!subjectAbbreviation && displaySubject) {
      subjectAbbreviation =
        computeInitials(displaySubject) ||
        displaySubject.slice(0, 3).toUpperCase();
    }

    return {
      id: dbLesson.lesson_id,
      period: dbLesson.period_number || 1,
      day: dayName,
      time: timeString,
      subject: displaySubject,
      subjectAbbreviation,
      subjectNameFull: subjectName || null,
      courseId: dbLesson.course_id || null,
      courseName,
      courseCode,
      isCourseLesson,
      teacher: teacherDisplay,
      teacherKurzels: teacherKurzelsDisplay,
      absentTeacherKurzels: Array.from(absentTeacherKurzelsSet),
      room: roomDisplay,
      attendanceStatus,
      isPast,
      isOngoing: isOngoing,
      subjectColor,
      // Provide raw subject bg color for inline styling in the component
      subjectBgHex: dbHex,
      status: dbLesson.is_cancelled
        ? "cancelled"
        : isSubstituteLesson
        ? "teacher_changed"
        : isAdditionalAssignment
        ? roomActuallyChanged
          ? "room_changed"
          : Array.isArray(begleitungKurzels) && begleitungKurzels.length
          ? "teacher_changed"
          : "normal"
        : "normal",
      adminComment: adminComment,
      classId: dbLesson.class_id || dbLesson.course_id || "",
      className: (dbLesson as any).class_name || courseName || null,
      startTime: lessonStart.toISOString(),
      endTime: lessonEnd.toISOString(),
      isPrimaryTeacher: (dbLesson as any).isPrimaryTeacher ?? true, // Default to true for non-teacher views
      isSubstituteTeacher: isSubstituteLesson,
      // Pass through substitute fields from database
      is_substitute: isProperSub,
      substitute_is_cancelled: dbLesson.substitute_is_cancelled,
      substitute_status: dbLesson.substitute_status,
      substitute_comment: dbLesson.substitute_comment,
      original_teacher_kurzel: dbLesson.original_teacher_kurzel,
    };
  });
}

/**
 * OPTIMIZED: Get lessons for a specific class/course for the current week
 * @param classId - The class ID (can be 'teacher' for teacher view)
 * @param schoolId - The school ID
 * @param weekStart - Start date of the week
 * @param schoolDaysData - Pre-fetched school days data (optional)
 * @param userProfile - Pre-fetched user profile (optional)
 * @returns Promise<Array of lessons>
 */
export async function getLessonsForWeek(
  classId: string,
  schoolId: string,
  weekStart: Date,
  schoolDaysData?: SchoolDay[],
  userProfile?: any,
): Promise<Lesson[]> {
  try {
    // Use pre-fetched school days or fetch if not provided
    const schoolDays = schoolDaysData || (await getSchoolDays(schoolId));

    // Calculate week range
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const weekStartStr = weekStart.toISOString().split("T")[0];
    const weekEndStr = weekEnd.toISOString().split("T")[0];

    let query = supabase
      .from("vw_react_lesson_details")
      .select("*")
      .eq("school_id", schoolId)
      .gte("lesson_date", weekStartStr)
      .lte("lesson_date", weekEndStr)
      .order("start_datetime", { ascending: true });

    // Advanced filters: teacher/room selectors (encoded in classId)
    let roomFilterKey: string | null = null;
    if (classId && classId.startsWith("teacher:")) {
      const teacherKurzel = classId.split(":")[1];
      // Filter lessons where the teacher_kurzels array contains the selected kürzel
      query = query.contains("teacher_kurzels", [teacherKurzel]);
    } else if (classId && classId.startsWith("teacher-id:")) {
      // Defer to teacher-id handling below (full union of primary/supporting)
    } else if (classId && classId.startsWith("room:")) {
      // We'll fetch week lessons and filter rooms client-side to avoid PostgREST filter escaping issues
      roomFilterKey = classId.split(":")[1];
    }

    // Filter by class or teacher depending on the view
    if (
      classId === "teacher" ||
      (classId && classId.startsWith("teacher-id:"))
    ) {
      // Use pre-fetched user profile or get from cache
      const profile = userProfile || (await getCachedUserProfile());
      const targetTeacherId = classId.startsWith("teacher-id:")
        ? classId.split(":")[1]
        : profile?.id;
      if (targetTeacherId) {
        // Query for lessons where user is primary teacher OR substitute primary teacher
        const { data: primaryLessons, error: primaryError } = await supabase
          .from("vw_react_lesson_details")
          .select("*")
          .eq("school_id", schoolId)
          .gte("lesson_date", weekStartStr)
          .lte("lesson_date", weekEndStr)
          .or(
            `assigned_teacher_id.eq.${targetTeacherId},substitute_primary_teacher_id.eq.${targetTeacherId}`,
          )
          .order("start_datetime", { ascending: true });

        if (primaryError) {
          console.error(
            "❌ Error fetching primary teacher lessons:",
            primaryError,
          );
          throw primaryError;
        }

        // Query for lessons where user is in teacher_ids array (supporting teacher) OR substitute supporting teacher
        const { data: supportingLessons, error: supportingError } =
          await supabase
            .from("vw_react_lesson_details")
            .select("*")
            .eq("school_id", schoolId)
            .gte("lesson_date", weekStartStr)
            .lte("lesson_date", weekEndStr)
            .or(
              `teacher_ids.cs.{${targetTeacherId}},substitute_begleitung_teacher_ids.cs.{${targetTeacherId}}`,
            )
            .order("start_datetime", { ascending: true });

        if (supportingError) {
          console.error(
            "❌ Error fetching supporting teacher lessons:",
            supportingError,
          );
          throw supportingError;
        }

        // Combine and deduplicate results, marking role and substitute status
        const allLessons = [
          ...(primaryLessons || []).map((lesson) => {
            const isSubTeacher =
              (lesson.substitute_type === "vertretung" ||
                lesson.substitute_type === "begleitung") &&
              (lesson.substitute_primary_teacher_id === targetTeacherId ||
                (lesson.substitute_begleitung_teacher_ids &&
                  lesson.substitute_begleitung_teacher_ids.includes(
                    targetTeacherId,
                  )));
            return {
              ...lesson,
              viewerTeacherId: targetTeacherId,
              isPrimaryTeacher:
                lesson.assigned_teacher_id === targetTeacherId ||
                lesson.substitute_primary_teacher_id === targetTeacherId,
              isSubstituteTeacher: isSubTeacher,
            };
          }),
          ...(supportingLessons || []).map((lesson) => ({
            ...lesson,
            viewerTeacherId: targetTeacherId,
            isPrimaryTeacher: false,
            isSubstituteTeacher:
              (lesson.substitute_type === "vertretung" ||
                lesson.substitute_type === "begleitung") &&
              (lesson.substitute_primary_teacher_id === targetTeacherId ||
                (lesson.substitute_begleitung_teacher_ids &&
                  lesson.substitute_begleitung_teacher_ids.includes(
                    targetTeacherId,
                  ))),
          })),
        ];

        const uniqueLessons = allLessons.filter(
          (lesson, index, self) =>
            index === self.findIndex((l) => l.lesson_id === lesson.lesson_id),
        );

        // Filter out lessons where the viewer is the assigned teacher but someone ELSE is substituting
        // (i.e., don't show lessons where another teacher is covering for you)
        const filteredLessons = uniqueLessons.filter((lesson) => {
          const isAssignedTeacher =
            lesson.assigned_teacher_id === targetTeacherId;
          const hasSubstitute =
            lesson.substitute_primary_teacher_id &&
            lesson.substitute_primary_teacher_id !== targetTeacherId;
          if (isAssignedTeacher && hasSubstitute) {
            return false; // Don't show lessons where someone else is substituting for this teacher
          }
          return true;
        });

        // Sort by start time
        const lessons = filteredLessons.sort(
          (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime),
        );

        // Enrich with substitution view for original/absent kürzels (similar to fetchTodaysLessons)
        const lessonIds = lessons.map((l) => l.lesson_id);
        let subsByLesson: Record<string, any> = {};
        if (lessonIds.length > 0) {
          try {
            const { data: subRows, error: subError } = await supabase
              .from("vw_react_lessons_needing_substitute")
              .select(
                "lesson_id, original_teacher_kurzel, absent_teacher_kurzels",
              )
              .in("lesson_id", lessonIds);
            if (!subError && Array.isArray(subRows)) {
              subRows.forEach((row: any) => {
                if (row && row.lesson_id) subsByLesson[row.lesson_id] = row;
              });
            } else if (subError) {
              try {
                console.error(
                  "❌ Error fetching substitution view for Klassenbuch (teacher):",
                  JSON.stringify(
                    subError,
                    Object.getOwnPropertyNames(subError),
                    2,
                  ),
                );
              } catch (e) {
                console.error(
                  "❌ Error fetching substitution view for Klassenbuch (teacher):",
                  subError,
                );
              }
            }
          } catch (e) {
            console.error(
              "❌ Exception enriching Klassenbuch lessons with substitutions (teacher):",
              e,
            );
          }
        }

        const enrichedLessons = lessons.map((lesson: any) => {
          const extra = subsByLesson[lesson.lesson_id] || {};
          return {
            ...lesson,
            original_teacher_kurzel:
              extra.original_teacher_kurzel ||
              lesson.original_teacher_kurzel ||
              null,
            absent_teacher_kurzels: Array.isArray(extra.absent_teacher_kurzels)
              ? extra.absent_teacher_kurzels
              : lesson.absent_teacher_kurzels || null,
          };
        });

        // Transform database lessons to our Lesson interface
        const transformedLessons: Lesson[] =
          await transformDatabaseLessonsOptimized(enrichedLessons, schoolDays);
        return transformedLessons;
      }
    } else if (
      classId &&
      !classId.startsWith("course-") &&
      !classId.startsWith("teacher:") &&
      !classId.startsWith("room:")
    ) {
      // For class view, filter by class_id directly from DB
      query = query.eq("class_id", classId);
    } else if (classId && classId.startsWith("course-")) {
      // For course view, filter by course_id
      query = query.eq("course_id", classId);
    }

    // Only execute this query for non-teacher views
    if (classId !== "teacher") {
      const { data: lessons, error } = await query;

      if (error) {
        try {
          console.error(
            "❌ Error fetching lessons:",
            JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
          );
        } catch (e) {
          console.error("❌ Error fetching lessons:", error);
        }
        throw error;
      }

      let filteredLessons = lessons || [];

      // If roomFilterKey was specified, filter client-side
      if (roomFilterKey) {
        filteredLessons = filteredLessons.filter((l: any) => {
          const roomNumber = l.room_number || "";
          const roomName = l.room_name || "";
          return roomNumber === roomFilterKey || roomName === roomFilterKey;
        });
      }

      // Add substitute lesson detection for class view (similar to teacher view logic)
      const lessonsWithSubstituteFlag = (filteredLessons || []).map(
        (lesson) => ({
          ...lesson,
          isSubstituteTeacher:
            lesson.substitute_type === "vertretung" ||
            lesson.substitute_type === "begleitung",
        }),
      );

      // Enrich with substitution view for original/absent kürzels (similar to fetchTodaysLessons)
      const lessonIds = lessonsWithSubstituteFlag.map((l: any) => l.lesson_id);
      let subsByLesson: Record<string, any> = {};
      if (lessonIds.length > 0) {
        try {
          const { data: subRows, error: subError } = await supabase
            .from("vw_react_lessons_needing_substitute")
            .select(
              "lesson_id, original_teacher_kurzel, absent_teacher_kurzels",
            )
            .in("lesson_id", lessonIds);
          if (!subError && Array.isArray(subRows)) {
            subRows.forEach((row: any) => {
              if (row && row.lesson_id) subsByLesson[row.lesson_id] = row;
            });
          } else if (subError) {
            try {
              console.error(
                "❌ Error fetching substitution view for Klassenbuch (class/room):",
                JSON.stringify(
                  subError,
                  Object.getOwnPropertyNames(subError),
                  2,
                ),
              );
            } catch (e) {
              console.error(
                "❌ Error fetching substitution view for Klassenbuch (class/room):",
                subError,
              );
            }
          }
        } catch (e) {
          console.error(
            "❌ Exception enriching Klassenbuch lessons with substitutions (class/room):",
            e,
          );
        }
      }
      const enrichedLessons = lessonsWithSubstituteFlag.map((lesson: any) => {
        const extra = subsByLesson[lesson.lesson_id] || {};
        return {
          ...lesson,
          original_teacher_kurzel:
            extra.original_teacher_kurzel ||
            lesson.original_teacher_kurzel ||
            null,
          absent_teacher_kurzels: Array.isArray(extra.absent_teacher_kurzels)
            ? extra.absent_teacher_kurzels
            : lesson.absent_teacher_kurzels || null,
        };
      });

      // Transform database lessons to our Lesson interface with optimized attendance checking
      const transformedLessons: Lesson[] =
        await transformDatabaseLessonsOptimized(enrichedLessons, schoolDays);
      return transformedLessons;
    }

    // Default fallback
    const transformedLessons: Lesson[] = [];

    return transformedLessons;
  } catch (error) {
    try {
      console.error(
        "💥 Error in getLessonsForWeek:",
        JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      );
    } catch (e) {
      console.error("💥 Error in getLessonsForWeek:", error);
    }
    return []; // Return empty array on error to prevent UI from breaking
  }
}

function normalizeDateString(input?: string | null): string | null {
  if (!input) return null;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function rangesOverlap(
  start: string,
  end: string,
  windowStart: string,
  windowEnd: string,
): boolean {
  return start <= windowEnd && end >= windowStart;
}

export async function getCalendarExceptionsForRange(
  schoolId: string,
  weekStart: Date,
  weekEnd: Date,
): Promise<CalendarException[]> {
  try {
    const weekStartIso = normalizeDateString(weekStart.toISOString());
    const weekEndIso = normalizeDateString(weekEnd.toISOString());
    if (!weekStartIso || !weekEndIso) return [];

    const [
      { data: schoolRows, error: schoolErr },
      { data: globalRows, error: globalErr },
    ] = await Promise.all([
      supabase
        .from("schedule_calendar_exceptions")
        .select(
          "id, start_date, end_date, date, type, allow_checkins, notes, source, school_id",
        )
        .eq("school_id", schoolId),
      supabase
        .from("public_holiday_and_breaks")
        .select("id, name, start_date, end_date, type, country, city, source")
        .lte("start_date", weekEndIso)
        .gte("end_date", weekStartIso)
        .order("start_date", { ascending: true }),
    ]);

    if (schoolErr) {
      console.error(
        "❌ Error fetching schedule_calendar_exceptions:",
        schoolErr,
      );
    }
    if (globalErr) {
      console.error("❌ Error fetching public_holiday_and_breaks:", globalErr);
    }

    const results: CalendarException[] = [];

    (schoolRows || []).forEach((row: any) => {
      const start =
        normalizeDateString(row.start_date) || normalizeDateString(row.date);
      const end =
        normalizeDateString(row.end_date) ||
        normalizeDateString(row.date) ||
        start;
      if (!start || !end) return;
      if (!rangesOverlap(start, end, weekStartIso, weekEndIso)) return;
      results.push({
        id: row.id,
        startDate: start,
        endDate: end,
        type: row.type || "school_closed",
        allowCheckins: row.allow_checkins === true,
        name: row.notes || "Schulfrei",
        source: row.source || null,
        isGlobal: false,
      });
    });

    (globalRows || []).forEach((row: any) => {
      const start = normalizeDateString(row.start_date);
      const end = normalizeDateString(row.end_date) || start;
      if (!start || !end) return;
      if (!rangesOverlap(start, end, weekStartIso, weekEndIso)) return;
      results.push({
        id: row.id,
        startDate: start,
        endDate: end,
        type: row.type || "holiday",
        allowCheckins: false,
        name: row.name || "Feiertag",
        source:
          row.source ||
          `${row.country || ""}${row.city ? ` / ${row.city}` : ""}`.trim() ||
          null,
        isGlobal: true,
      });
    });

    return results.sort((a, b) => a.startDate.localeCompare(b.startDate));
  } catch (error) {
    console.error("💥 Error fetching calendar exceptions:", error);
    return [];
  }
}
